'use client'

import { useEffect, useState } from 'react'
import { useChainId } from 'wagmi'
import { optimismSepolia } from 'viem/chains'
import { type Address } from 'viem'
import { usePrivy, useWallets } from '@privy-io/react-auth'
import {
  isGasSponsorshipSupported,
  isFeatureSupported,
} from './config/gasSponsorship'

/**
 * Hook for using Privy's native Smart Wallet with built-in gas sponsorship
 * This leverages Privy's existing Smart Wallet features instead of custom implementation
 */
export const usePrivySmartWallet = () => {
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { user, authenticated, sendTransaction } = usePrivy()
  const { wallets, ready } = useWallets()
  const chainId = useChainId()

  const hasGasSponsorship = isGasSponsorshipSupported(chainId || 0)
  const smartWalletAddress = user?.smartWallet?.address as Address | undefined
  const hasSmartWallet = !!smartWalletAddress

  // Find the Smart Wallet from the wallets list
  const smartWallet = wallets.find(
    wallet =>
      wallet.walletClientType === 'privy' &&
      wallet.address === smartWalletAddress
  )

  useEffect(() => {
    if (hasGasSponsorship && authenticated && hasSmartWallet && ready) {
      setIsReady(true)
      setError(null)
    } else {
      setIsReady(false)
      if (!hasGasSponsorship) {
        setError('Gas sponsorship not supported on this network')
      } else if (!authenticated) {
        setError('Not authenticated')
      } else if (!hasSmartWallet) {
        setError('No Smart Wallet available')
      } else if (!ready) {
        setError('Wallets not ready')
      } else {
        setError(null)
      }
    }
  }, [
    hasGasSponsorship,
    authenticated,
    hasSmartWallet,
    smartWalletAddress,
    ready,
    smartWallet,
    wallets,
  ])

  /**
   * Execute a transaction using Privy's Smart Wallet
   * Uses Privy's sendTransaction which should automatically use the Smart Wallet when available
   */
  const executeSmartWalletTransaction = async (
    to: Address,
    data: `0x${string}`,
    value: bigint = BigInt(0)
  ): Promise<`0x${string}`> => {
    if (!isReady || !smartWalletAddress) {
      throw new Error('Smart Wallet not ready')
    }

    try {
      // Use Privy's sendTransaction - it should automatically use Smart Wallet
      const txResponse = await sendTransaction({
        to,
        data,
        value: value.toString(),
      })

      return txResponse.hash as `0x${string}`
    } catch (error) {
      console.error('❌ Smart Wallet transaction failed:', error)
      throw error
    }
  }

  /**
   * Get gas sponsorship info (simplified for Privy Smart Wallet)
   */
  const getGasSponsorshipInfo = async () => {
    if (!isReady) {
      throw new Error('Smart Wallet not ready')
    }

    return {
      sponsored: true,
      provider: 'Privy Smart Wallet',
      estimatedSavings: 'Gas fees sponsored by Privy Smart Wallet',
      maxFeePerGas: 'Handled automatically',
      maxPriorityFeePerGas: 'Handled automatically',
      gasLimit: 'Estimated automatically',
    }
  }

  return {
    // Status
    isReady,
    error,

    // Wallet info
    smartWalletAddress,
    hasSmartWallet,
    hasGasSponsorship: isReady, // Smart Wallets have built-in gas sponsorship

    // Actions
    executeGaslessTransaction: executeSmartWalletTransaction,
    getGasSponsorshipInfo,

    // For debugging
    user: authenticated ? user : null,
    smartWallet: smartWallet || null,
    availableWallets: wallets.length,
  }
}
